<template>
  <div
    class="a4-page"
    style="
      position: relative;
      background-color: var(--color-white);
      border: 1px solid var(--color-light-gray);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      width: 100%;
      aspect-ratio: 1/1.414;
      padding: 40px;
      overflow: hidden;
    "
  >
    <!-- Plant Components Title -->
    <h4 class="h5 fw-bold mb-3">
      {{ $t("project.previewAndReport.sections.plantComponents") }}
    </h4>
    <!-- Plant Components Section -->
    <plant-components-section
      v-if="shouldShowPlantComponents"
      :report-type="reportType"
      :selected-panels="selectedPanels"
      :selected-inverters="selectedInverters"
      :selected-batteries="selectedBatteries"
      :selectedEVChargers="selectedEVChargers"
      :selected-meters="selectedMeters"
      :get-panel-device-count="getPanelDeviceCount"
      :get-inverter-device-count="getInverterDeviceCount"
      :get-battery-device-count="getBatteryDeviceCount"
      :getEVChargerDeviceCount="getEVChargerDeviceCount"
      :get-meter-device-count="getMeterDeviceCount"
    />

    <!-- Yield estimates Title -->
    <h4 v-if="shouldShowConsumption" class="h5 fw-bold mb-3">
      {{ $t("project.previewAndReport.sections.yieldEstimates") }}
    </h4>

    <!-- Consumption Section -->
    <consumption-section
      v-if="shouldShowConsumption"
      :consumption-data="consumptionData"
    />

    <!-- Cash Flow Section -->
    <cash-flow-section v-if="shouldShowCashFlow" :project-data="projectData" />

    <!-- Price Section -->
    <price-section
      v-if="shouldShowPrice"
      :project-data="projectData"
      :payment-type="paymentType"
      :report-type="reportType"
    />

    <!-- Date and Signature Section -->
    <date-signature-section
      v-if="shouldShowSignature"
      :report-type="reportType"
    />

    <!-- Footer -->
    <report-footer :report-type="reportType" />

    <!-- Page Number -->
    <div class="position-absolute bottom-0 end-0" style="padding: 20px 40px">
      <small class="text-muted" style="font-size: 12px">2</small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, ComputedRef } from "vue";
import type {
  Project,
  Panel,
  Inverter,
  Battery,
  EVCharger,
  Meter,
} from "@/types/projectTypes";
import PlantComponentsSection from "../sections/PlantComponentsSection.vue";
import ConsumptionSection from "../sections/ConsumptionSection.vue";
import CashFlowSection from "../sections/CashFlowSection.vue";
import PriceSection from "../sections/PriceSection.vue";
import DateSignatureSection from "../sections/DateSignatureSection.vue";
import ReportFooter from "../sections/ReportFooter.vue";

export default defineComponent({
  name: "ReportPage2",
  components: {
    PlantComponentsSection,
    ConsumptionSection,
    CashFlowSection,
    PriceSection,
    DateSignatureSection,
  },
  props: {
    projectData: {
      type: Object as PropType<Project | null>,
      required: true,
    },
    includeSections: {
      type: Object as PropType<Record<string, boolean>>,
      required: true,
    },
    consumptionData: {
      type: Object as PropType<{
        totalF1: number;
        totalF2: number;
        totalF3: number;
        grandTotal: number;
      }>,
      required: true,
    },
    selectedPanels: {
      type: Array as PropType<Panel[]>,
      required: true,
    },
    selectedInverters: {
      type: Array as PropType<Inverter[]>,
      required: true,
    },
    selectedBatteries: {
      type: Array as PropType<Battery[]>,
      required: true,
    },
    selectedEVChargers: {
      type: Array as PropType<EVCharger[]>,
      required: true,
    },
    selectedMeters: {
      type: Array as PropType<Meter[]>,
      required: true,
    },
    getPanelDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    getInverterDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    getBatteryDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    getEVChargerDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    getMeterDeviceCount: {
      type: Function as PropType<(id: number) => ComputedRef<number>>,
      required: true,
    },
    paymentType: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const reportType = computed(() => {
      if (props.projectData?.service_type === "complete_solution") {
        return `${props.projectData.service_type}.${props.paymentType}`;
      }
      return props.projectData?.service_type || "product_supply";
    });

    const shouldShowPlantComponents = computed(() => {
      return (
        props.includeSections.panels ||
        props.includeSections.inverters ||
        props.includeSections.batteries ||
        props.includeSections.evChargers ||
        props.includeSections.meters
      );
    });

    const shouldShowConsumption = computed(() => {
      return props.projectData?.service_type === "complete_solution";
    });

    const shouldShowCashFlow = computed(() => {
      return (
        props.projectData?.service_type === "complete_solution" &&
        props.includeSections.cashFlow
      );
    });

    const shouldShowPrice = computed(() => {
      return props.projectData?.service_type !== "complete_solution";
    });

    const shouldShowSignature = computed(() => {
      return props.projectData?.service_type !== "complete_solution";
    });

    return {
      reportType,
      shouldShowPlantComponents,
      shouldShowConsumption,
      shouldShowCashFlow,
      shouldShowPrice,
      shouldShowSignature,
    };
  },
});
</script>
